import { useEffect, useState } from "react";
import { CreditCard, Loader2, CheckCircle2, XCircle } from "lucide-react";
import api, { extractError } from "@/api/client";

/**
 * MyPay wallet top-up button.
 *
 * Flow:
 *  1. Customer enters an amount (LYD) and optionally picks a gateway.
 *  2. We POST /api/payments/mypay/wallet/topup/create → get {payment_url, custom}.
 *  3. We open the payment_url in the SAME tab (MyPay hosts card/OTP entry).
 *  4. MyPay redirects back to /wallet/mypay/return?custom=... — that page polls
 *     /wallet/topup/{custom}/status until the webhook has settled.
 *
 * The `custom` id is saved in localStorage so refreshes don't lose track of the
 * in-flight top-up.
 */
const GATEWAY_LABELS = {
  "yasr-pay":    { name: "يسر باي",     hint: "Yasr Pay" },
  "masrafi-pay": { name: "مصرفي باي",   hint: "Masrafi Pay" },
  "moamalat":    { name: "بطاقة مصرفية", hint: "Visa / MasterCard (معاملات)" },
  "edfali":      { name: "ادفعلي",       hint: "Edfali" },
  "sahara":      { name: "صحارى",        hint: "Sahara" },
};

const DEFAULT_AMOUNTS = [5, 10, 20, 50, 100];

export default function MyPayTopupButton({ onSuccess }) {
  const [status, setStatus] = useState({ loading: true, enabled: false, env: "sandbox", gateways: [], min_amount: 1, max_amount: 5000, fee_percent: 0 });
  const [amount, setAmount] = useState(20);
  const [customAmount, setCustomAmount] = useState("");
  const [gateway, setGateway] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/payments/mypay/status")
      .then(({ data }) => { if (mounted) setStatus({ loading: false, ...data }); })
      .catch(() => { if (mounted) setStatus((s) => ({ ...s, loading: false, enabled: false })); });
    return () => { mounted = false; };
  }, []);

  const chosenAmount = customAmount !== "" ? parseFloat(customAmount) : amount;
  const amountValid = !isNaN(chosenAmount) && chosenAmount >= (status.min_amount || 1) && chosenAmount <= (status.max_amount || 5000);
  const feePercent = Number(status.fee_percent || 0);
  const feeAmount = amountValid ? Math.round(chosenAmount * feePercent) / 100 : 0;
  const totalCharge = amountValid ? Math.round((chosenAmount + feeAmount) * 100) / 100 : 0;

  const startTopup = async () => {
    if (!amountValid) { setErr(`المبلغ يجب أن يكون بين ${status.min_amount} و ${status.max_amount} د.ل`); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/payments/mypay/wallet/topup/create", {
        amount: chosenAmount,
        payment_gateway: gateway || undefined,
      });
      // Remember the pending top-up so /wallet/mypay/return can poll for status.
      try { localStorage.setItem("mypay:pending", JSON.stringify({ custom: data.custom, amount: chosenAmount, at: Date.now() })); } catch { /* noop */ }
      // Full-page redirect — MyPay hosts the card entry / OTP screens.
      window.location.href = data.payment_url;
    } catch (e) {
      setErr(extractError(e) || "تعذّر بدء عملية الدفع");
      setBusy(false);
    }
  };

  if (status.loading) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-[var(--border)]" data-testid="mypay-loading">
        <Loader2 size={16} className="animate-spin" /> جارِ تحميل خيارات الدفع...
      </div>
    );
  }

  if (!status.enabled) {
    return null; // Hidden entirely if the merchant hasn't wired the credentials.
  }

  return (
    <div className="space-y-3 p-3 rounded-2xl bg-white border border-[var(--border)]" data-testid="mypay-topup-panel">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-light text-primary-brand flex items-center justify-center shrink-0">
          <CreditCard size={18} />
        </div>
        <div className="flex-1">
          <div className="font-black text-sm">شحن عبر MyPay</div>
          <div className="text-[11px] text-[var(--text-secondary)]">
            دفع إلكتروني عبر يسر / مصرفي / معاملات / إدفعلي / صحارى
            {status.env === "sandbox" && <span className="mx-1 px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[9px] font-black">تجريبي</span>}
          </div>
        </div>
      </div>

      {/* Amount picker */}
      <div>
        <div className="text-xs font-bold text-[var(--text-secondary)] mb-1.5">المبلغ (د.ل):</div>
        <div className="grid grid-cols-5 gap-1.5">
          {DEFAULT_AMOUNTS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => { setAmount(a); setCustomAmount(""); }}
              className={`py-2 rounded-lg text-sm font-black transition ${(customAmount === "" && amount === a) ? "bg-primary-brand text-white" : "bg-white border border-[var(--border)] hover:border-primary-brand"}`}
              data-testid={`mypay-amt-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
        <input
          type="number"
          inputMode="decimal"
          min={status.min_amount}
          max={status.max_amount}
          placeholder="مبلغ آخر..."
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          className="w-full mt-1.5 px-3 py-2 rounded-lg border border-[var(--border)] text-sm"
          data-testid="mypay-custom-amount"
        />
      </div>

      {/* Optional gateway picker */}
      <div>
        <div className="text-xs font-bold text-[var(--text-secondary)] mb-1.5">اختر البوابة (اختياري):</div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => setGateway("")}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold col-span-2 ${gateway === "" ? "bg-primary-brand text-white" : "bg-white border border-[var(--border)] hover:border-primary-brand"}`}
            data-testid="mypay-gw-auto"
          >
            اترك MyPay يعرض كل الخيارات
          </button>
          {(status.gateways || []).map((g) => {
            const label = GATEWAY_LABELS[g] || { name: g, hint: g };
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGateway(g)}
                className={`px-2 py-1.5 rounded-lg text-xs font-bold ${gateway === g ? "bg-primary-brand text-white" : "bg-white border border-[var(--border)] hover:border-primary-brand"}`}
                data-testid={`mypay-gw-${g}`}
                title={label.hint}
              >
                {label.name}
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5" data-testid="mypay-error">
          {err}
        </div>
      )}

      {amountValid && feePercent > 0 && (
        <div className="rounded-xl border border-primary-brand/20 bg-primary-light/40 p-2.5 text-xs" data-testid="mypay-fee-breakdown">
          <div className="flex justify-between py-0.5">
            <span>يُضاف إلى محفظتك:</span>
            <span className="font-black">{chosenAmount.toFixed(2)} د.ل</span>
          </div>
          <div className="flex justify-between py-0.5 text-[var(--text-secondary)]">
            <span>رسوم الدفع ({feePercent}%):</span>
            <span className="font-bold">{feeAmount.toFixed(2)} د.ل</span>
          </div>
          <div className="flex justify-between py-1 border-t border-primary-brand/20 mt-1 font-black text-primary-brand">
            <span>الإجمالي المطلوب دفعه:</span>
            <span data-testid="mypay-total-charge">{totalCharge.toFixed(2)} د.ل</span>
          </div>
        </div>
      )}

      <button
        onClick={startTopup}
        disabled={busy || !amountValid}
        className="w-full py-2.5 rounded-xl bg-primary-brand hover:bg-blue-800 text-white font-black flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="mypay-start-btn"
      >
        {busy ? (<><Loader2 size={16} className="animate-spin" /> جارِ التحويل...</>) : (<>ادفع {amountValid ? totalCharge.toFixed(2) : ""} د.ل عبر MyPay</>)}
      </button>
    </div>
  );
}

/**
 * Small component embedded in the /wallet/mypay/return page. Polls the status
 * endpoint until the webhook has landed (max ~30s), then calls onSuccess with
 * the fresh wallet balance.
 */
export function MyPayReturnStatus({ custom, onResolved }) {
  const [state, setState] = useState({ loading: true, status: "pending", credited: false, amount: 0, gateway: "" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!custom) return;
    let stopped = false;
    let tick = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/mypay/wallet/topup/${custom}/status`);
        if (stopped) return;
        setState({ loading: false, ...data });
        setTries((n) => n + 1);
        if (data.credited || ["completed", "failed", "cancelled"].includes(String(data.status || "").toLowerCase())) {
          onResolved?.(data);
          return;
        }
      } catch { /* keep polling */ }
      tick += 1;
      if (tick > 30) { onResolved?.({ status: "timeout" }); return; }
      setTimeout(poll, 1000);
    };
    poll();
    return () => { stopped = true; };
  }, [custom]);

  if (state.loading) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> جارِ التحقق من الدفع...</div>;
  if (state.credited || state.status === "completed") return <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={18}/> تم الشحن بنجاح — {state.amount} د.ل</div>;
  if (["failed", "cancelled"].includes(String(state.status || "").toLowerCase())) return <div className="flex items-center gap-2 text-red-700"><XCircle size={18}/> فشل الدفع أو تم إلغاؤه</div>;
  return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> ننتظر تأكيد MyPay ({tries})...</div>;
}
